/**
 * State + thunks for the Creator Settings → Billing tab.
 *
 * Pattern A (hybrid) — same as OrgSettingsBillingPage: plan summary +
 * change / cancel / resume live in-app; Stripe Customer Portal owns
 * invoices, payment methods, billing email, tax IDs.
 *
 * The subscription itself lives on currentUser.privateData
 * .creatorSubscription (loaded by fetchCurrentUser); this duck only
 * tracks the in-progress / error state of the four mutation actions.
 */

import { fetchCurrentUser } from '../../ducks/user.duck';
import { fetchStripeAccount } from '../../ducks/stripeConnectAccount.duck';
import { payoutsEnabled } from '../../ducks/stripe.duck';
import { creatorSubscriptionsAPI } from '../../api';
import { storableError } from '../../util/errors';

// ================ Action types ================ //

export const CANCEL_REQUEST = 'app/CreatorSettingsBillingPage/CANCEL_REQUEST';
export const CANCEL_SUCCESS = 'app/CreatorSettingsBillingPage/CANCEL_SUCCESS';
export const CANCEL_ERROR = 'app/CreatorSettingsBillingPage/CANCEL_ERROR';

export const RESUME_REQUEST = 'app/CreatorSettingsBillingPage/RESUME_REQUEST';
export const RESUME_SUCCESS = 'app/CreatorSettingsBillingPage/RESUME_SUCCESS';
export const RESUME_ERROR = 'app/CreatorSettingsBillingPage/RESUME_ERROR';

export const CHANGE_PLAN_REQUEST = 'app/CreatorSettingsBillingPage/CHANGE_PLAN_REQUEST';
export const CHANGE_PLAN_SUCCESS = 'app/CreatorSettingsBillingPage/CHANGE_PLAN_SUCCESS';
export const CHANGE_PLAN_ERROR = 'app/CreatorSettingsBillingPage/CHANGE_PLAN_ERROR';

export const OPEN_PORTAL_REQUEST = 'app/CreatorSettingsBillingPage/OPEN_PORTAL_REQUEST';
export const OPEN_PORTAL_SUCCESS = 'app/CreatorSettingsBillingPage/OPEN_PORTAL_SUCCESS';
export const OPEN_PORTAL_ERROR = 'app/CreatorSettingsBillingPage/OPEN_PORTAL_ERROR';

// Payout history (Stripe balance_transactions on the connected account)
export const FETCH_PAYOUTS_REQUEST = 'app/CreatorSettingsBillingPage/FETCH_PAYOUTS_REQUEST';
export const FETCH_PAYOUTS_SUCCESS = 'app/CreatorSettingsBillingPage/FETCH_PAYOUTS_SUCCESS';
export const FETCH_PAYOUTS_ERROR = 'app/CreatorSettingsBillingPage/FETCH_PAYOUTS_ERROR';
export const FETCH_PAYOUTS_MORE_REQUEST = 'app/CreatorSettingsBillingPage/FETCH_PAYOUTS_MORE_REQUEST';
export const FETCH_PAYOUTS_MORE_SUCCESS = 'app/CreatorSettingsBillingPage/FETCH_PAYOUTS_MORE_SUCCESS';
export const FETCH_PAYOUTS_MORE_ERROR = 'app/CreatorSettingsBillingPage/FETCH_PAYOUTS_MORE_ERROR';

// ================ Reducer ================ //

const initialState = {
  cancelInProgress: false,
  cancelError: null,
  resumeInProgress: false,
  resumeError: null,
  changePlanInProgress: false,
  changePlanError: null,
  openPortalInProgress: false,
  openPortalError: null,
  payouts: [],
  payoutsLoading: false,
  payoutsLoaded: false,
  payoutsError: null,
  payoutsHasMore: false,
  payoutsNextCursor: null,
  payoutsLoadingMore: false,
};

export default function reducer(state = initialState, action = {}) {
  const { type, payload } = action;
  switch (type) {
    case CANCEL_REQUEST:
      return { ...state, cancelInProgress: true, cancelError: null };
    case CANCEL_SUCCESS:
      return { ...state, cancelInProgress: false };
    case CANCEL_ERROR:
      return { ...state, cancelInProgress: false, cancelError: payload };

    case RESUME_REQUEST:
      return { ...state, resumeInProgress: true, resumeError: null };
    case RESUME_SUCCESS:
      return { ...state, resumeInProgress: false };
    case RESUME_ERROR:
      return { ...state, resumeInProgress: false, resumeError: payload };

    case CHANGE_PLAN_REQUEST:
      return { ...state, changePlanInProgress: true, changePlanError: null };
    case CHANGE_PLAN_SUCCESS:
      return { ...state, changePlanInProgress: false };
    case CHANGE_PLAN_ERROR:
      return { ...state, changePlanInProgress: false, changePlanError: payload };

    case OPEN_PORTAL_REQUEST:
      return { ...state, openPortalInProgress: true, openPortalError: null };
    case OPEN_PORTAL_SUCCESS:
      return { ...state, openPortalInProgress: false };
    case OPEN_PORTAL_ERROR:
      return { ...state, openPortalInProgress: false, openPortalError: payload };

    case FETCH_PAYOUTS_REQUEST:
      return { ...state, payoutsLoading: true, payoutsError: null };
    case FETCH_PAYOUTS_SUCCESS:
      return {
        ...state,
        payoutsLoading: false,
        payoutsLoaded: true,
        payouts: payload.items || [],
        payoutsHasMore: !!payload.hasMore,
        payoutsNextCursor: payload.nextCursor || null,
      };
    case FETCH_PAYOUTS_ERROR:
      return { ...state, payoutsLoading: false, payoutsError: payload };

    case FETCH_PAYOUTS_MORE_REQUEST:
      return { ...state, payoutsLoadingMore: true, payoutsError: null };
    case FETCH_PAYOUTS_MORE_SUCCESS:
      return {
        ...state,
        payoutsLoadingMore: false,
        payouts: [...state.payouts, ...(payload.items || [])],
        payoutsHasMore: !!payload.hasMore,
        payoutsNextCursor: payload.nextCursor || null,
      };
    case FETCH_PAYOUTS_MORE_ERROR:
      return { ...state, payoutsLoadingMore: false, payoutsError: payload };

    default:
      return state;
  }
}

// ================ Thunks ================ //

export const cancelCreatorSubscription = (immediate = false) => async dispatch => {
  dispatch({ type: CANCEL_REQUEST });
  try {
    const response = await creatorSubscriptionsAPI.cancelSubscription(immediate);
    await dispatch(fetchCurrentUser());
    dispatch({ type: CANCEL_SUCCESS });
    return response;
  } catch (e) {
    dispatch({ type: CANCEL_ERROR, payload: storableError(e) });
    throw e;
  }
};

export const resumeCreatorSubscription = () => async dispatch => {
  dispatch({ type: RESUME_REQUEST });
  try {
    const response = await creatorSubscriptionsAPI.resumeSubscription();
    await dispatch(fetchCurrentUser());
    dispatch({ type: RESUME_SUCCESS });
    return response;
  } catch (e) {
    dispatch({ type: RESUME_ERROR, payload: storableError(e) });
    throw e;
  }
};

export const changeCreatorPlan = (targetTier, interval) => async dispatch => {
  dispatch({ type: CHANGE_PLAN_REQUEST });
  try {
    const response = await creatorSubscriptionsAPI.changeSubscriptionTier(
      targetTier,
      interval
    );
    await dispatch(fetchCurrentUser());
    dispatch({ type: CHANGE_PLAN_SUCCESS });
    return response;
  } catch (e) {
    dispatch({ type: CHANGE_PLAN_ERROR, payload: storableError(e) });
    throw e;
  }
};

/**
 * Free -> paid: create a Stripe Checkout session and redirect. The
 * subscription is created on Stripe's hosted page; the webhook +
 * inline write then populate privateData.creatorSubscription before
 * the user lands back on /settings/billing?upgrade=success.
 */
export const startCreatorCheckout = (targetTier, interval) => async dispatch => {
  dispatch({ type: CHANGE_PLAN_REQUEST });
  try {
    const response = await creatorSubscriptionsAPI.createSubscriptionCheckout(
      targetTier,
      interval
    );
    const url = response?.checkoutUrl;
    if (!url) throw new Error('Checkout URL missing in response');
    dispatch({ type: CHANGE_PLAN_SUCCESS });
    return url;
  } catch (e) {
    dispatch({ type: CHANGE_PLAN_ERROR, payload: storableError(e) });
    throw e;
  }
};

// Strip axios envelope. The REST API client uses axios for GET — the
// actual JSON body lives at response.data; our endpoints further wrap
// in `{ data: { ... } }`.
const unwrap = response => {
  const body = response?.data ?? response ?? {};
  return body?.data ?? body;
};

export const fetchCreatorPayoutHistory = () => async dispatch => {
  dispatch({ type: FETCH_PAYOUTS_REQUEST });
  try {
    const res = await creatorSubscriptionsAPI.getPayoutHistory();
    const { items, hasMore, nextCursor } = unwrap(res);
    dispatch({
      type: FETCH_PAYOUTS_SUCCESS,
      payload: { items: items || [], hasMore, nextCursor },
    });
  } catch (e) {
    dispatch({ type: FETCH_PAYOUTS_ERROR, payload: storableError(e) });
  }
};

export const loadMoreCreatorPayouts = () => async (dispatch, getState) => {
  const { payoutsNextCursor, payoutsHasMore, payoutsLoadingMore } =
    getState().CreatorSettingsBillingPage;
  if (!payoutsHasMore || !payoutsNextCursor || payoutsLoadingMore) return;
  dispatch({ type: FETCH_PAYOUTS_MORE_REQUEST });
  try {
    const res = await creatorSubscriptionsAPI.getPayoutHistory({
      startingAfter: payoutsNextCursor,
    });
    const { items, hasMore, nextCursor } = unwrap(res);
    dispatch({
      type: FETCH_PAYOUTS_MORE_SUCCESS,
      payload: { items: items || [], hasMore, nextCursor },
    });
  } catch (e) {
    dispatch({ type: FETCH_PAYOUTS_MORE_ERROR, payload: storableError(e) });
  }
};

export const openCreatorCustomerPortal = returnPath => async dispatch => {
  dispatch({ type: OPEN_PORTAL_REQUEST });
  try {
    const response = await creatorSubscriptionsAPI.createCustomerPortalSession(
      returnPath
    );
    dispatch({ type: OPEN_PORTAL_SUCCESS });
    return response?.portalUrl || null;
  } catch (e) {
    dispatch({ type: OPEN_PORTAL_ERROR, payload: storableError(e) });
    throw e;
  }
};

// ================ loadData ================ //

export const loadData = (params = {}) => async (dispatch, getState) => {
  // Subscription lives on currentUser.privateData.creatorSubscription;
  // Stripe Connect account lives on its own slice; payout history is
  // fetched from Stripe via our own endpoint.
  //
  // Order matters: fetch currentUser first so we know whether the user
  // even has a Stripe Connect account before we try to fetch it. Without
  // the guard, sdk.stripeAccount.fetch() 404s loudly for every user who
  // hasn't connected yet — same guard the old StripePayoutPage loadData
  // had. The payout-history endpoint already returns an empty list when
  // there's no Stripe account, so it's safe to call unconditionally.
  await dispatch(fetchCurrentUser());

  const currentUser = getState().user?.currentUser;
  const hasStripeAccount = !!currentUser?.stripeAccount;
  const currentUserId = currentUser?.id?.uuid;
  // Refresh the seller's cached payoutsEnabled flag when they return
  // from Stripe-hosted onboarding (their account state likely changed).
  // This is the single moment we burn a Stripe API call per seller; from
  // then on every buyer pageview reads from the cached publicData.
  const returnedFromStripe = params.returnURLType === 'success';

  await Promise.all([
    hasStripeAccount
      ? dispatch(fetchStripeAccount()).catch(() => null)
      : Promise.resolve(null),
    dispatch(fetchCreatorPayoutHistory()).catch(() => null),
    returnedFromStripe && currentUserId
      ? dispatch(payoutsEnabled(currentUserId, { refresh: true })).catch(() => null)
      : Promise.resolve(null),
  ]);
  return null;
};
